// Verdict editor for one factory build: status + a note explaining it
// ("Прошел проверку, но не работал Google AdMob"). Shared by the Build tab
// (developers) and the QA page — both hit the same endpoint.
import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Select } from './Select';
import {
  BUILD_QA_LABELS, BUILD_QA_STATUSES, BUILD_QA_STYLES, buildQaStatus, setBuildQa,
  type BuildQaStatus,
} from '../api/build-qa';

const OPTIONS = BUILD_QA_STATUSES.map((s) => ({ value: s, label: BUILD_QA_LABELS[s] }));

export function BuildQaChip({ status, note }: { status: string | null | undefined; note?: string | null }) {
  const s = buildQaStatus(status);
  return (
    <span
      title={note || undefined}
      className={`px-2 py-0.5 text-xs rounded-full whitespace-nowrap ${BUILD_QA_STYLES[s]}`}
    >
      {BUILD_QA_LABELS[s]}
    </span>
  );
}

export function BuildQaControl({
  buildId,
  status,
  note,
  reviewer,
  reviewedAt,
  onSaved,
}: {
  buildId: string;
  status: string | null | undefined;
  note: string | null;
  reviewer?: string | null;
  reviewedAt?: number | null;
  onSaved?: () => void;
}) {
  const [draftNote, setDraftNote] = useState(note ?? '');
  const [editingNote, setEditingNote] = useState(false);
  const current = buildQaStatus(status);

  // Server state wins whenever the row is refetched, unless the box is open —
  // that would wipe what the reviewer is typing.
  useEffect(() => {
    if (!editingNote) setDraftNote(note ?? '');
  }, [note, editingNote]);

  const save = useMutation({
    mutationFn: (patch: { qa_status?: BuildQaStatus; qa_note?: string }) => setBuildQa(buildId, patch),
    onSuccess: () => { setEditingNote(false); onSaved?.(); },
  });

  return (
    <div className="space-y-1.5 min-w-[13rem]">
      <Select
        size="sm"
        value={current}
        options={OPTIONS}
        disabled={save.isPending}
        onChange={(v) => save.mutate({ qa_status: v as BuildQaStatus })}
        className={BUILD_QA_STYLES[current]}
      />

      {editingNote ? (
        <div className="space-y-1">
          <textarea
            rows={3}
            value={draftNote}
            maxLength={2000}
            autoFocus
            onChange={(e) => setDraftNote(e.target.value)}
            placeholder="Что проверяли и что сломалось"
            className="block w-full px-2 py-1 text-xs border border-border-strong rounded-md focus:outline-none focus:ring-primary focus:border-primary bg-surface text-content"
          />
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={save.isPending}
              onClick={() => save.mutate({ qa_note: draftNote })}
              className="text-xs text-primary hover:underline disabled:opacity-50"
            >
              {save.isPending ? 'Сохраняю…' : 'Сохранить'}
            </button>
            <button
              type="button"
              onClick={() => { setEditingNote(false); setDraftNote(note ?? ''); }}
              className="text-xs text-content-subtle hover:underline"
            >
              Отмена
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setEditingNote(true)}
          className="block w-full text-left text-xs text-content-muted hover:text-content"
          title={note || undefined}
        >
          {note ? note : <span className="text-content-subtle">+ описание проверки</span>}
        </button>
      )}

      {(reviewer || reviewedAt) && !editingNote && (
        <p className="text-[11px] text-content-subtle">
          {reviewer || 'QA'}
          {reviewedAt ? ` · ${new Date(reviewedAt * 1000).toLocaleString()}` : ''}
        </p>
      )}
      {save.isError && (
        <p className="text-[11px] text-red-500">
          {save.error instanceof Error ? save.error.message : 'Не удалось сохранить'}
        </p>
      )}
    </div>
  );
}
